import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import { ResponseCodes } from "./types/responseCodes";
import { Dictionary } from "./types/interfaces/dictionary";
import { eventEmitter } from "./helpers/eventEmitter";
import { REFRESH_PAGE, redirectToRefresh, redirectToSignIn } from "./helpers/authSession";

export const addAuthHeader = (header: Dictionary<string> | null = null) => {
  if (header === null) {
    header = {};
  }

  const token = localStorage.getItem("xAuthToken");
  if (token !== null) {
    header["Authorization"] = `Bearer ${token}`;
  }

  return header;
};

// стабильный отпечаток устройства бэк develop требует X-Device-Fingerprint на auth и throttлит по нему
export const getDeviceFingerprint = (): string => {
  let fp = localStorage.getItem("xDeviceFingerprint");
  if (!fp) {
    fp = uuidv4();
    localStorage.setItem("xDeviceFingerprint", fp);
  }
  return fp;
};

export const mainAPI = axios.create({
  baseURL: process.env.REACT_APP_API_MAIN_URL,
  headers: addAuthHeader(),
});

export const tripsAPI = axios.create({
  baseURL: process.env.REACT_APP_API_TRIPS_URL,
  headers: addAuthHeader(),
});

mainAPI.interceptors.response.use(
  (response) => response,
  (error) => parseErrorCode(error)
);
tripsAPI.interceptors.response.use(
  (response) => response,
  (error) => parseErrorCode(error)
);

// X-Device-Fingerprint нужен только на auth эндпоинтах бэк ограничивает частоту по нему token refresh otp
// на остальные запросы не шлём чтобы не ловить CORS preflight на nginx статике media
const AUTH_FINGERPRINT_PATHS = ["users/token", "users/otp"];
mainAPI.interceptors.request.use((config) => {
  const url = config.url || "";
  if (AUTH_FINGERPRINT_PATHS.some((p) => url.includes(p))) {
    config.headers["X-Device-Fingerprint"] = getDeviceFingerprint();
  }
  return config;
});

const parseErrorCode = (error: any) => {
  if (error.response) {
    const code = error.response.status;
    const url = (error.config?.url || "").split("?")[0];
    const isRefresh = /(?:^|\/)users\/token\/refresh\/?$/.test(url);
    const isSignIn = /(?:^|\/)users\/(?:token|otp\/(?:create_otp|verify_otp))\/?$/.test(url);
    if (isRefresh && [400, 401, 403, 404].includes(code)) {
      delete mainAPI.defaults.headers.Authorization;
      delete mainAPI.defaults.headers.common.Authorization;
      delete tripsAPI.defaults.headers.Authorization;
      delete tripsAPI.defaults.headers.common.Authorization;
      redirectToSignIn();
      return Promise.reject(error.response);
    }
    const textLength = 200;
    let message = "Неизвестная ошибка";
    switch (code) {
      case ResponseCodes.UNAUTHORIZED:
        if (!isSignIn) {
          if (window.location.pathname !== REFRESH_PAGE) redirectToRefresh();
          return Promise.reject(error.response);
        }
        message = "Ошибка авторизации";
        break;
      case ResponseCodes.FORBIDDEN:
        message = "Доступ запрещен";
        break;
      case ResponseCodes.BAD_REQUEST:
        message = "Ошибка в запросе";
        break;
      default:
        console.warn(error.response);
    }
    const data = error.response.data;
    const detail = data?.detail || data?.error || data || message;
    message = typeof detail === "string" ? detail : JSON.stringify(detail);
    eventEmitter.emit("alert", {
      message: message.slice(0, textLength),
      severity: "error",
    });
  }

  return Promise.reject(error.response || error);
};
